import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { IconButton, useToast } from '@/components/ui';
import { useSendFeedback } from '@/lib/api';
import type { FeedbackRating, UUID } from '@/lib/api/types';
import './AnswerFeedback.css';

export interface AnswerFeedbackProps {
  /** The persisted interaction being rated — the feedback endpoint's target. */
  interactionId: UUID;
}

/**
 * Thumbs up/down on a single answer — the evaluation loop of the Knowledge
 * chat. The selected vote stays pressed (aria-pressed) so the user sees what
 * they sent; a failed send surfaces a toast and leaves the selection untouched,
 * so the vote can simply be retried.
 */
export function AnswerFeedback({ interactionId }: AnswerFeedbackProps): JSX.Element {
  const [selected, setSelected] = useState<FeedbackRating | null>(null);
  const { toast } = useToast();
  const send = useSendFeedback();

  const rate = (rating: FeedbackRating): void => {
    send
      .mutateAsync({ interactionId, input: { rating } })
      .then(() => setSelected(rating))
      .catch(() =>
        toast({
          tone: 'error',
          title: 'Falha ao enviar avaliação',
          description: 'Não foi possível registrar seu feedback. Tente novamente.',
        }),
      );
  };

  // O IconButton compartilhado traz o alvo de toque de 40px que os botões
  // próprios de 28px não tinham; só a cor de pressionado é deste componente.
  return (
    <div className="chat__feedback" role="group" aria-label="Avaliar resposta">
      <IconButton
        size="sm"
        className="chat__feedback-btn"
        label="Resposta útil"
        icon={<ThumbsUp />}
        aria-pressed={selected === 1}
        disabled={send.isPending}
        onClick={() => rate(1)}
      />
      <IconButton
        size="sm"
        className="chat__feedback-btn"
        label="Resposta não útil"
        icon={<ThumbsDown />}
        aria-pressed={selected === -1}
        disabled={send.isPending}
        onClick={() => rate(-1)}
      />
    </div>
  );
}
